import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import useRegisterForm from "../../hooks/userRegisterForm";

const ALLOWED_ROLES = [
  "Estudiante",
  "Administrador",
  "Administrativo",
  "Docente",
  "Docente de planta",
  "Docente de cátedra",
];

// Encabezados EXACTOS que tu importador espera
const TEMPLATE_HEADERS = [
  "nombre",
  "apellidos",
  "tipocc",
  "numcc",
  "correo",
  "contrasena",
  "rol",
  "facultad",
  "programa",
];

const toLower = (s) => (s || "").toString().toLowerCase().trim();

function canonRol(raw) {
  const v = toLower(raw);
  if (!v) return "";

  if (v === "admin" || v === "administrador") return "Administrador";
  if (v === "administrativo") return "Administrativo";
  if (v === "estudiante" || v === "student" || v === "alumno")
    return "Estudiante";

  // Variantes de docente
  if (v.includes("docente")) {
    if (v.includes("planta")) return "Docente de planta";
    if (v.includes("catedra") || v.includes("cátedra"))
      return "Docente de cátedra";
    return "Docente"; // legacy o genérico
  }

  return "";
}

// Mapa flexible de encabezados -> campo destino
const HEADER_MAP = [
  { match: ["nombre", "nombres"], to: "nombre" },
  { match: ["apellidos", "apellido", "apellido(s)"], to: "apellidos" },
  {
    match: [
      "tipocc",
      "tipo doc",
      "tipo documento",
      "tipo de documento",
      "tipodoc",
    ],
    to: "tipocc",
  },
  {
    match: [
      "numcc",
      "numero documento",
      "número documento",
      "documento",
      "doc",
      "no documento",
    ],
    to: "numcc",
  },
  { match: ["correo", "email", "e-mail", "mail"], to: "correo" },
  {
    match: ["contrasena", "contraseña", "password", "clave"],
    to: "contrasena",
  },
  {
    match: ["rol", "role", "perfil", "ocupacion", "ocupación", "cargo"],
    to: "rol",
  },
  { match: ["facultad"], to: "facultad" },
  { match: ["programa", "program"], to: "programa" },
];

function resolveField(header) {
  const h = toLower(header);
  for (const rule of HEADER_MAP) {
    if (rule.match.some((m) => toLower(m) === h)) return rule.to;
  }
  return null;
}

export default function UploadUsuariosExcel({ onClose, onDone }) {
  const { registerMany } = useRegisterForm(); // reusa tu hook
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  // { total, inserted, dupCount, dupDetails: [{row, correo}], otherErrorsCount, fatalMessage?, localInvalid: [{row, reason}] }

  // === NUEVO: Descargar plantilla de una sola hoja con encabezados exactos ===
  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    // Solo la fila de encabezados (sin filas de ejemplo)
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);

    // Opcional: anchos de columna y congelar la fila de encabezados
    ws["!cols"] = TEMPLATE_HEADERS.map((h) => ({
      wch: Math.max(12, h.length + 2),
    }));
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    XLSX.utils.book_append_sheet(wb, ws, "usuarios");
    XLSX.writeFile(wb, "plantilla_usuarios.xlsx");
  };

  const parseExcel = async (file) => {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data);
    const ws = wb.Sheets[wb.SheetNames[0]];

    // Obtenemos como objetos {header:value}
    const rows = XLSX.utils.sheet_to_json(ws, { defval: "" }); // primera fila = headers

    // Mapeo de encabezados flexibles a campos destino
    const parsed = [];
    const localInvalid = []; // filas que no cumplen requisitos mínimos

    rows.forEach((r, idx) => {
      const out = {};
      Object.keys(r).forEach((k) => {
        const field = resolveField(k);
        if (field) out[field] = r[k];
      });

      // Normalizaciones
      out.nombre = (out.nombre || "").toString().trim();
      out.apellidos = (out.apellidos || "").toString().trim();
      out.tipocc = (out.tipocc || "").toString().trim();
      out.numcc = (out.numcc || "").toString().trim();
      out.correo = toLower(out.correo);
      out.contrasena = (out.contrasena || "").toString();
      out.rol = canonRol(out.rol);
      out.facultad = (out.facultad || "").toString().trim();
      out.programa = (out.programa || "").toString().trim();

      // Validaciones mínimas (coinciden con backend)
      const requiredMissing =
        !out.nombre ||
        !out.correo ||
        !out.contrasena ||
        !out.rol ||
        !out.facultad;

      const rolNoValido = out.rol && !ALLOWED_ROLES.includes(out.rol);

      if (requiredMissing || rolNoValido) {
        localInvalid.push({
          row: idx + 2, // +2 por header (fila 1) y base 1
          reason: requiredMissing
            ? "Campos obligatorios faltantes (nombre, correo, contrasena, rol, facultad)."
            : `Rol inválido: ${out.rol}`,
        });
        return; // no agregamos al lote
      }

      parsed.push(out);
    });

    return { parsed, localInvalid };
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const { parsed, localInvalid } = await parseExcel(file);

      if (parsed.length === 0 && localInvalid.length > 0) {
        setResult({
          total: localInvalid.length,
          inserted: 0,
          dupCount: 0,
          dupDetails: [],
          otherErrorsCount: 0,
          localInvalid,
          fatalMessage: "No hay filas válidas para enviar.",
        });
        setUploading(false);
        return;
      }

      // Enviamos solo filas válidas; el hook hará los POST 1x1 (o batched)
      const res = await registerMany(parsed);

      const total = parsed.length;
      const inserted = res?.inserted ?? total - (res?.errors?.length || 0);

      const dupRows = (res?.errors || []).filter(
        (e) =>
          e.code === "DUPLICATE" ||
          /duplicad|registrad|existe/i.test(e.message || "")
      );
      const otherErrors = (res?.errors || []).filter(
        (e) => !dupRows.includes(e)
      );

      const dupDetails = dupRows.map((e) => ({
        row: e.row, // tu hook suele retornar i+2
        correo: parsed[e.row - 2]?.correo || "(sin correo)",
      }));

      setResult({
        total,
        inserted,
        dupCount: dupRows.length,
        dupDetails: dupDetails.slice(0, 20),
        otherErrorsCount: otherErrors.length,
        localInvalid, // mostramos también errores locales (faltantes/rol)
      });
    } catch (err) {
      setResult({
        total: 0,
        inserted: 0,
        dupCount: 0,
        dupDetails: [],
        otherErrorsCount: 1,
        fatalMessage: err.message || "Error al procesar el archivo.",
      });
    } finally {
      setUploading(false);
    }
  };

  const closeAll = () => {
    setResult(null);
    onDone?.();
    onClose?.();
  };

  return (
    <>
      {/* Modal de selección y carga */}
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5">
          <h2 className="text-lg font-bold mb-3">Subir usuarios desde Excel</h2>
          <p className="text-sm text-gray-600 mb-3">
            La primera hoja debe tener columnas:&nbsp;
            <b>
              nombre, apellidos, tipocc, numcc, correo, contrasena, rol,
              facultad, programa
            </b>
            .
          </p>

          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={downloadTemplate}
              className="px-3 py-2 rounded border text-sm"
            >
              Descargar plantilla
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="flex-1 border rounded p-2 text-sm"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded border text-sm"
              disabled={uploading}
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              className="px-4 py-2 rounded bg-green-700 text-white text-sm disabled:opacity-60"
              disabled={uploading}
            >
              {uploading ? "Cargando..." : "Cargar Excel"}
            </button>
          </div>
        </div>
      </div>

      {/* Modal de resultado */}
      {result && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold mb-2">Resultado de la carga</h3>

            {result.fatalMessage ? (
              <p className="text-red-600">{result.fatalMessage}</p>
            ) : (
              <>
                <p className="text-sm mb-1">
                  <b>Total válidos enviados:</b> {result.total}
                </p>
                <p className="text-sm mb-1">
                  <b>Insertados:</b> {result.inserted}
                </p>
                <p className="text-sm mb-1">
                  <b>Duplicados en BD:</b> {result.dupCount}
                </p>
                {!!result.otherErrorsCount && (
                  <p className="text-sm mb-1 text-orange-700">
                    <b>Otros errores (BD/API):</b> {result.otherErrorsCount}
                  </p>
                )}

                {!!result.localInvalid?.length && (
                  <div className="mt-3">
                    <p className="text-sm font-semibold">
                      Filas inválidas (archivo) — faltantes/rol:
                    </p>
                    <ul className="text-sm list-disc ml-5 max-h-40 overflow-auto">
                      {result.localInvalid.slice(0, 20).map((d, i) => (
                        <li key={i}>
                          Fila {d.row}: {d.reason}
                        </li>
                      ))}
                    </ul>
                    {result.localInvalid.length > 20 && (
                      <p className="text-xs text-gray-500 mt-1">
                        (+{result.localInvalid.length - 20} más…)
                      </p>
                    )}
                  </div>
                )}

                {result.dupCount > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-semibold">
                      Primeros duplicados:
                    </p>
                    <ul className="text-sm list-disc ml-5 max-h-40 overflow-auto">
                      {result.dupDetails.map((d, i) => (
                        <li key={i}>
                          Fila {d.row} — {d.correo}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-gray-500 mt-1">
                      Esos registros no se cargaron porque el correo ya existe.
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={closeAll}
                className="px-4 py-2 rounded bg-green-700 text-white text-sm"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
